/**
 * RoomSelection — native staging room picker (left column) (lifted from
 * `common/StagingFlow/RoomSelection`, rewired onto the module working context +
 * plain-JS room arrays + the file-hub thumbnail join). Nav-only (no DTO write).
 */

import { Box, CircularProgress, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import { MUIBadge } from 'components/common/MUIBadge'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { BLUE_500, BOX_SHADOW_MD, GRAY_100, GRAY_700 } from 'constants/styling/theme'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { pressableFocusSx, pressableProps } from '../../../components/pressableProps'
import { useStagingWorkingState } from '../StagingWorkingState.context'

const MIN_IMAGE_WIDTH = 250
const IMAGE_HEIGHT = 150

/** Room status label when no room type is selected yet. */
enum RoomStatus {
  NOT_SET = 'NOT_SET',
}

/**
 * One room-sized frame saying its picture is being prepared.
 *
 * Two states share it, because to the client they are one thing — "this photo is
 * not ready yet": a room whose derivative has not landed, and a photo whose room
 * does not exist yet because the upload is still running.
 */
const PreparingRoomTile: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  return (
    <Stack
      data-cy="pf-revamp-room-preparing"
      alignItems="center"
      justifyContent="center"
      gap={1}
      sx={{
        minWidth: MIN_IMAGE_WIDTH,
        height: IMAGE_HEIGHT,
        borderRadius: '1rem',
        boxSizing: 'border-box',
        border: '4px solid transparent',
        borderColor: isActive ? BLUE_500 : 'transparent',
        backgroundColor: GRAY_100,
      }}
    >
      <CircularProgress
        size="2rem"
        aria-label={t('purchase_flow_revamp:configuration.staging.preview_loading')}
      />
      <Typography variant="text-sm" color={GRAY_700} textAlign="center">
        {t('purchase_flow_revamp:configuration.staging.preview_loading')}
      </Typography>
    </Stack>
  )
}

export interface RoomSelectionProps {
  /** Uploaded visuals that have no room yet — one frame each. */
  pendingRooms?: number
}

export const RoomSelection: React.FC<RoomSelectionProps> = ({ pendingRooms = 0 }) => {
  const { allRooms, activeRoomIndex, setActiveRoomIndex } = useStagingWorkingState()

  const { t } = useTranslation(['virtual_furniture_room_type', 'style_selection_order_step', 'common', 'purchase_flow_revamp'])

  // Track visited rooms so the incomplete-config warning only shows once touched.
  const [touchedRooms, setTouchedRooms] = useState<Set<number>>(new Set())

  const totalRooms = allRooms.length
  const completedRooms = useMemo(
    () => allRooms.reduce((count, room) => (room.isComplete ? count + 1 : count), 0),
    [allRooms]
  )

  const changeSelectedRoom = (roomIndex: number) => {
    setTouchedRooms(new Set(touchedRooms.add(activeRoomIndex)))
    setActiveRoomIndex(roomIndex)
  }

  return (
    <SectionedBorderBox
      height="100%"
      title={`${t('style_selection_order_step:rooms_stage')} ${completedRooms}/${totalRooms}`}
    >
      <Stack gap={2} width="100%" alignItems="center">
        {allRooms.map((room, index) => {
          const image = room.images[0]
          const showWarning = !room.isComplete && touchedRooms.has(index)
          const isActive = activeRoomIndex === index

          return (
            <Box
              key={room.key}
              {...pressableProps(() => changeSelectedRoom(index))}
              aria-label={t('purchase_flow_revamp:configuration.staging.room_aria', { number: index + 1 })}
              aria-pressed={isActive}
              sx={{
                ...pressableFocusSx,
                cursor: 'pointer',
                borderRadius: '1rem',
                position: 'relative',
                minWidth: MIN_IMAGE_WIDTH,
                ':hover': { boxShadow: BOX_SHADOW_MD },
              }}
            >

              {/* A room exists as soon as the photo is uploaded, but its picture
                  is a DERIVATIVE the backend generates afterwards — the poll runs
                  up to a minute. Until it lands `image.url` is undefined, and an
                  `<img>` with no src renders as an empty box that reads as a
                  broken room. Hold the same footprint and say it is coming. */}
              {image?.url
                ? (
                  <img
                    alt={image.id}
                    src={image.url}
                    style={{
                      minWidth: MIN_IMAGE_WIDTH,
                      height: IMAGE_HEIGHT,
                      display: 'block',
                      contain: 'content',
                      borderRadius: '1rem',
                      boxSizing: 'border-box',
                      border: '4px solid transparent',
                      borderColor: isActive ? BLUE_500 : 'transparent',
                    }}
                  />
                )
                : <PreparingRoomTile isActive={isActive} />
              }

              <Box position="absolute" top="1rem" right="1rem">
                <MUIBadge
                  icon={showWarning ? <ErrorOutline /> : undefined}
                  label={
                    room.roomTypes.length
                      ? room.roomTypes.map((type) => t(type)).join(', ')
                      : t(`style_selection_order_step:status:${RoomStatus.NOT_SET}`)
                  }
                  color={showWarning ? 'orange' : 'gray'}
                  sx={{ maxWidth: MIN_IMAGE_WIDTH - 20, textOverflow: 'ellipsis' }}
                />
              </Box>

            </Box>
          )
        })}

        {/* The reconcile waits for quiescence before it adds a room, so a photo
            still on its way has none — and on a list that already holds a room
            that is indistinguishable from the upload being ignored. */}
        {Array.from({ length: pendingRooms }, (_, index) => (
          <PreparingRoomTile key={`pending-${index}`} isActive={false} />
        ))}

      </Stack>
    </SectionedBorderBox>
  )
}
